/*
 * FILE: unified_ui/src/stores/orders.ts
 * PURPOSE: Pinia store for the orders list. Loads from Core via webBridge NATS proxy.
 * OWNS: orders state, selection, status mapping, API-driven load/refresh.
 */
import { defineStore } from 'pinia'
import { call } from '@/lib/botSuiteClient'
import { CMD, type SearchRunHistoryItem, type JobProgress } from '@/lib/commands'

export type OrderStatus = 'draft' | 'in_progress' | 'ready' | 'cancelled' | 'failed'

export type Order = {
  id: string
  title: string
  purpose?: string | null
  createdAt: string
  status: OrderStatus
  hasPdf: boolean
  pdfLabel?: string | null
  itemCount?: number
  /** Live progress snapshot from parser (null for terminal states). */
  progress?: JobProgress | null
  /** Raw record for detail views that need more data. */
  raw?: SearchRunHistoryItem
}

function mapStatus(coreStatus: string): OrderStatus {
  switch (coreStatus) {
    case 'artifacts_ready':
    case 'completed':
      return 'ready'
    case 'running':
    case 'starting':
    case 'finalizing':
      return 'in_progress'
    case 'cancelled':
    case 'cancelled_local':
      return 'cancelled'
    case 'failed':
    case 'timed_out':
    case 'cancel_failed':
    case 'cancel_error':
    case 'aborted':
      return 'failed'
    default:
      return 'in_progress'
  }
}

type State = {
  orders: Order[]
  loading: boolean
  loaded: boolean
  error: string | null
  pollInterval: ReturnType<typeof setInterval> | null
  _polling: boolean
  _cancellingIds: Set<string>
}

export const useOrdersStore = defineStore('orders', {
  state: (): State => ({
    orders: [],
    loading: false,
    loaded: false,
    error: null,
    pollInterval: null,
    _polling: false,
    _cancellingIds: new Set<string>(),
  }),
  getters: {
    active: (s) => s.orders.filter((o) => o.status === 'in_progress' || o.status === 'draft'),
    hasActive: (s) => s.orders.some(o => o.status === 'in_progress' || o.status === 'draft'),
    activeOrder: (s) => s.orders.find(o => o.status === 'in_progress' || o.status === 'draft') ?? null,
    ready: (s) => s.orders.filter((o) => o.status === 'ready'),
    closed: (s) => s.orders.filter((o) => o.status === 'cancelled' || o.status === 'failed'),
    findById: (s) => (id: string) => s.orders.find((o) => o.id === id) ?? null,
  },
  actions: {
    async load() {
      if (this.loaded || this.loading) return
      await this._fetch()
    },

    async refresh(silent?: boolean) {
      this._polling = silent ?? false
      if (!this._polling) {
        this.loaded = false
        this.loading = false
      }
      await this._fetch()
    },

    async _fetch() {
      if (!this._polling) {
        this.loading = true
      }
      this.error = null

      const result = await call<Record<string, never>, SearchRunHistoryItem[]>(
        CMD.searchHistory,
      )

      if (!result.success) {
        this.error = result.error
        this.loading = false
        return
      }

      // Merge backend results with existing orders by id to preserve
      // optimistically prepended orders that the backend may not yet have.
      const existing = new Map(this.orders.map((o) => [o.id, o]))
      for (const item of result.data ?? []) {
        const prev = existing.get(item.runId)
        // Preserve optimistically cancelled orders during cancel race.
        // If we just set status='cancelled' locally but the server hasn't
        // caught up yet, don't let the poll overwrite it with 'running'.
        if (prev?.status === 'cancelled' && this._cancellingIds.has(item.runId)) {
          const serverStatus = mapStatus(item.status)
          if (serverStatus === 'cancelled' || serverStatus === 'failed') {
            // Server confirms terminal — accept it and clear the flag
            this._cancellingIds.delete(item.runId)
            // fall through to normal set below
          } else {
            // Server still shows running — keep our optimistic cancel
            continue
          }
        }

        existing.set(item.runId, {
          id: item.runId,
          title: item.query,
          purpose: item.why ?? null,
          createdAt: item.createdAt,
          status: mapStatus(item.status),
          hasPdf: item.pdfFileId != null,
          pdfLabel: item.pdfFilename ?? null,
          progress: item.progress ?? null,
          raw: item,
        })
      }

      this.orders = Array.from(existing.values()).sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      )

      this.loading = false
      this.loaded = true
    },

    startPolling() {
      if (this.pollInterval) return
      this.pollInterval = setInterval(() => { void this.refresh(true) }, 5000)
    },

    stopPolling() {
      if (this.pollInterval) {
        clearInterval(this.pollInterval)
        this.pollInterval = null
      }
    },

    prepend(order: Order) {
      this.orders = [order, ...this.orders]
    },

    markCancelling(searchId: string) {
      this._cancellingIds.add(searchId)
    },
  },
})
